import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { Observable } from 'rxjs';

import {
    ApiFragment,
    BaseNestedFieldDefinition,
    BatchFieldReadHelper,
    ComplexMapFieldValue,
    ExtendedBaseProfileField,
    FieldReadHelper,
    FormValue,
    FragmentId,
    Identifier,
    PartialRecord,
    ShBaseGenericInputFieldComponent,
    UserFunction,
    UserService
} from '@stylehaven/components';

@Component({
    selector: 'med-thesaurus-field',
    templateUrl: './thesaurus-input-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThesaurusInputFieldComponent extends ShBaseGenericInputFieldComponent implements OnInit {
    public submitSuggestionFunction$: Observable<boolean>;

    public thesaurusField: BaseNestedFieldDefinition;
    public thesaurusFragmentIds: Identifier[];
    public initialSuggestions: string[] = [];
    public activeRecord?: ApiFragment;

    @Input() multiple?: boolean = false;
    @Input() thesaurusFieldDefinition: BaseNestedFieldDefinition;

    constructor(
        protected fieldHelper: FieldReadHelper,
        private readonly userService: UserService
    ) {
        super();
    }

    init(profileField: ExtendedBaseProfileField, fragments: ApiFragment[], control: UntypedFormControl): void {
        this.thesaurusField = this.thesaurusFieldDefinition
            ? this.thesaurusFieldDefinition
            : profileField.FieldDefinition;

        this.thesaurusFragmentIds = fragments
            .map(fragment => fragment.getFragmentId())
            .filter((id): id is FragmentId => !!id);

        this.multiple
            ? this.setComplexValues(profileField, fragments, control)
            : this.setSimpleValues(profileField, fragments, control);

        // Only do for first, since if we're trying to fetch suggestions for multiple fragments, we're doing something wrong
        if (fragments.length === 1) {
            this.initialSuggestions = fragments[0].metadata.Context?.Suggestions
                ? fragments[0].metadata.Context?.Suggestions.filter(suggestion =>
                      suggestion.Structural.FieldDefinition.DottedKey.startsWith(profileField.DottedKey)
                  ).map(suggestion => suggestion.Descriptive.Title)
                : [];

            this.activeRecord = fragments[0];
        }
        this.initSubmitSuggestionFunction();
    }

    private setComplexValues(
        profileField: ExtendedBaseProfileField,
        fragments: PartialRecord[],
        control: UntypedFormControl
    ): void {
        const values: FormValue<ComplexMapFieldValue>[] = this.isBatchEdit
            ? (BatchFieldReadHelper.getBatchFieldValue(
                  profileField.FieldDefinition
              ) as FormValue<ComplexMapFieldValue>[])
            : this.fieldHelper.getMergedComplexFieldValue(profileField.FieldDefinition, fragments);
        const filtered: FormValue<ComplexMapFieldValue>[] = values.filter(formValue => !!formValue.value);
        control.setValue(filtered);
    }

    private setSimpleValues(
        profileField: ExtendedBaseProfileField,
        fragments: PartialRecord[],
        control: UntypedFormControl
    ): void {
        const values: FormValue<any>[] = this.isBatchEdit
            ? (BatchFieldReadHelper.getBatchFieldValue(profileField.FieldDefinition) as FormValue<any>[])
            : this.fieldHelper.getMergedThesaurusFieldValue(profileField.FieldDefinition, fragments);

        const filtered = values.filter(formValue => !!formValue.value);
        control.setValue(filtered);
    }

    private initSubmitSuggestionFunction(): void {
        this.submitSuggestionFunction$ = this.userService.hasUserFunction(UserFunction.submitThesaurusSuggestions);
    }
}
